import React, { useRef, useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import {
  Box,
  Typography,
  Stack,
  Spinner,
  Button,
} from '@fortress-technology-solutions/fortress-component-library/Atoms';
import MessagingHubItem from './MessagingHubItem';
import intlMessages from './messages';
import type { MessagingHubListProps } from './types';

function MessagingHubList({
  conversations,
  isLoadingMore,
  onLoadMore,
  hasMore,
  onRefresh,
  selectedConversationId,
  onSelectConversation,
  isSearching,
  isNeedsHelpBadgeShown,
}: MessagingHubListProps): React$Node {
  const scrollContainerRef = useRef(null);

  const handleLoadMore = useCallback(() => {
    const container = scrollContainerRef.current;
    const previousScrollHeight = container ? container.scrollHeight : 0;

    const result = onLoadMore();

    if (result && typeof result.then === 'function') {
      result.then(() => {
        requestAnimationFrame(() => {
          if (!container) return;
          const addedHeight = container.scrollHeight - previousScrollHeight;
          if (addedHeight > 0) {
            const scrollAmount = Math.min(addedHeight, 200);
            container.scrollBy({
              top: scrollAmount,
              behavior: 'smooth',
            });
          }
        });
      });
    }
  }, [onLoadMore]);

  if (conversations.length === 0 && !hasMore) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="300px"
      >
        <Typography variant="bodyMedium">
          <FormattedMessage {...intlMessages.noMessagesFound} />
        </Typography>
      </Box>
    );
  }

  return (
    <Stack>
      <Box
        ref={scrollContainerRef}
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          overflow: 'auto',
          maxHeight: 'calc(100vh - 100px)',
          '&::-webkit-scrollbar': {
            display: 'none',
          },
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
        }}
      >
        {conversations.map((conversation) => (
          <MessagingHubItem
            key={conversation.conversationId}
            conversation={conversation}
            isSelected={selectedConversationId === conversation.conversationId}
            onSelect={() => onSelectConversation(conversation.conversationId)}
            isNeedsHelpBadgeShown={isNeedsHelpBadgeShown}
          />
        ))}
        {hasMore && (
          <Box display="flex" justifyContent="center" alignItems="center" p={2}>
            {isLoadingMore ? (
              <Spinner />
            ) : (
              <Button
                variant="text"
                onClick={handleLoadMore}
                disabled={isLoadingMore}
              >
                <FormattedMessage {...intlMessages.loadMore} />
              </Button>
            )}
          </Box>
        )}
      </Box>
      {isSearching && conversations.length > 0 && (
        <Box px={1} py={1}>
          <Typography variant="bodySmall" color="text.secondary" align="center">
            <FormattedMessage {...intlMessages.searchResultsHint} />
          </Typography>
        </Box>
      )}
    </Stack>
  );
}

export default MessagingHubList;
